import { createRng, type Rng } from '../random.ts';
import type { RhythmConfig, NoteValue } from './config.ts';
import { engravePhrase, splitWithinMeasure, writtenValue } from './engrave.ts';
import { getTimeSignature, groupStartBeats, resolveGrouping, type TimeSignature } from './meter.ts';
import { phraseFeatures } from './features.ts';
import { expectedTapTicks, type RhythmEvent, type RhythmPhrase, type RhythmTuplet } from './phrase.ts';

/**
 * Phrase generation. Measures are built beat by beat from a library of common
 * rhythmic cells (the way rhythm is taught), plus longer notes that start on a
 * beat. Then, only if enabled: some notes become rests, and some neighbouring
 * notes merge across a beat or barline into one longer sound — which the
 * engraver writes with a tie. Triplet cells (when enabled) record their group;
 * rests may fall inside a triplet but never fill it, and ties never touch one.
 * Everything is seeded, so a seed always yields the same phrases (server-side
 * generation and grading stay reproducible).
 */

interface Cell {
  /** Durations in ticks, filling `beats` whole beats. */
  durations: number[];
  beats: number;
  needs: NoteValue[];
  dotted?: boolean;
  /** Triplet groups in the cell: [offset, length] in ticks from the cell's start. */
  tuplets?: [number, number][];
  weight: number;
}

const QUARTER_BEAT: Cell[] = [
  { durations: [48], beats: 1, needs: ['quarter'], weight: 5 },
  { durations: [24, 24], beats: 1, needs: ['eighth'], weight: 5 },
  { durations: [24, 12, 12], beats: 1, needs: ['eighth', 'sixteenth'], weight: 2 },
  { durations: [12, 12, 24], beats: 1, needs: ['eighth', 'sixteenth'], weight: 2 },
  { durations: [12, 24, 12], beats: 1, needs: ['eighth', 'sixteenth'], weight: 1 },
  { durations: [12, 12, 12, 12], beats: 1, needs: ['sixteenth'], weight: 2 },
  { durations: [36, 12], beats: 1, needs: ['eighth', 'sixteenth'], dotted: true, weight: 2 },
  { durations: [12, 36], beats: 1, needs: ['eighth', 'sixteenth'], dotted: true, weight: 1 },
  // Longer notes starting on a beat.
  { durations: [96], beats: 2, needs: ['half'], weight: 3 },
  { durations: [144], beats: 3, needs: ['half'], dotted: true, weight: 2 },
  { durations: [192], beats: 4, needs: ['whole'], weight: 2 },
  { durations: [72, 24], beats: 2, needs: ['quarter', 'eighth'], dotted: true, weight: 3 },
  // Triplets: eighth-note triplets fill a beat; a quarter-note triplet fills two.
  { durations: [16, 16, 16], beats: 1, needs: ['eighth'], tuplets: [[0, 48]], weight: 4 },
  { durations: [32, 16], beats: 1, needs: ['quarter', 'eighth'], tuplets: [[0, 48]], weight: 2 },
  { durations: [16, 32], beats: 1, needs: ['quarter', 'eighth'], tuplets: [[0, 48]], weight: 1 },
  { durations: [32, 32, 32], beats: 2, needs: ['quarter'], tuplets: [[0, 96]], weight: 2 },
];

/** 2/2: the beat is a half note; each half can also be filled with quarter-level cells. */
const HALF_BEAT: Cell[] = [
  { durations: [96], beats: 1, needs: ['half'], weight: 4 },
  { durations: [72, 24], beats: 1, needs: ['quarter', 'eighth'], dotted: true, weight: 2 },
  { durations: [192], beats: 2, needs: ['whole'], weight: 2 },
  { durations: [144, 48], beats: 2, needs: ['half', 'quarter'], dotted: true, weight: 2 },
  // A quarter-note triplet fills one half-note beat.
  { durations: [32, 32, 32], beats: 1, needs: ['quarter'], tuplets: [[0, 96]], weight: 3 },
];

const DOTTED_QUARTER_BEAT: Cell[] = [
  { durations: [72], beats: 1, needs: ['quarter'], weight: 5 },
  { durations: [48, 24], beats: 1, needs: ['quarter', 'eighth'], weight: 4 },
  { durations: [24, 48], beats: 1, needs: ['quarter', 'eighth'], weight: 2 },
  { durations: [24, 24, 24], beats: 1, needs: ['eighth'], weight: 5 },
  { durations: [36, 12, 24], beats: 1, needs: ['eighth', 'sixteenth'], dotted: true, weight: 1 },
  { durations: [12, 12, 24, 24], beats: 1, needs: ['eighth', 'sixteenth'], weight: 2 },
  { durations: [24, 12, 12, 24], beats: 1, needs: ['eighth', 'sixteenth'], weight: 1 },
  { durations: [24, 24, 12, 12], beats: 1, needs: ['eighth', 'sixteenth'], weight: 2 },
  { durations: [12, 12, 12, 12, 12, 12], beats: 1, needs: ['sixteenth'], weight: 1 },
  { durations: [144], beats: 2, needs: ['half'], weight: 3 },
];

function allowed(cell: Cell, config: RhythmConfig): boolean {
  if (cell.dotted && !config.dotted) return false;
  if (cell.tuplets && !config.triplets) return false;
  return cell.needs.every((v) => config.noteValues.includes(v));
}

function weightedPick(rng: Rng, cells: Cell[]): Cell {
  const total = cells.reduce((sum, c) => sum + c.weight, 0);
  let roll = rng.next() * total;
  for (const cell of cells) {
    roll -= cell.weight;
    if (roll < 0) return cell;
  }
  return cells[cells.length - 1]!;
}

/** Cells usable for a beat of this meter. For 2/2 each half-note beat may also be two quarter-level cells. */
function beatCells(ts: TimeSignature, config: RhythmConfig): Cell[] {
  if (ts.kind === 'compound') return DOTTED_QUARTER_BEAT.filter((c) => allowed(c, config));
  if (ts.denominator === 2) {
    const quarterCells = QUARTER_BEAT.filter((c) => c.beats === 1 && allowed(c, config));
    const pairs: Cell[] = [];
    for (const a of quarterCells)
      for (const b of quarterCells) {
        const tuplets: [number, number][] = [...(a.tuplets ?? []), ...(b.tuplets ?? []).map(([o, l]): [number, number] => [o + 48, l])];
        pairs.push({ durations: [...a.durations, ...b.durations], beats: 1, needs: [], weight: (a.weight * b.weight) / 5, ...(tuplets.length ? { tuplets } : {}) });
      }
    return [...HALF_BEAT.filter((c) => allowed(c, config)), ...pairs];
  }
  return QUARTER_BEAT.filter((c) => allowed(c, config));
}

/**
 * Whether a cell of `beats` beats may start on `beat`: it stays inside one beat
 * group, or covers whole groups exactly. So the grouping shows in the rhythm —
 * half notes on 1 or 3 in 4/4 (2+2), a dotted half then a half in 5/4 (3+2),
 * "quarter, half" anywhere in 3/4.
 */
function respectsGrouping(groupStarts: number[], beatsPerMeasure: number, beat: number, beats: number): boolean {
  const end = beat + beats;
  const crosses = groupStarts.some((s) => s > beat && s < end);
  if (!crosses) return true;
  return groupStarts.includes(beat) && (end === beatsPerMeasure || groupStarts.includes(end));
}

function generateMeasure(ts: TimeSignature, cells: Cell[], rng: Rng, groupStarts: number[]): { durations: number[]; tuplets: RhythmTuplet[] } {
  const durations: number[] = [];
  const tuplets: RhythmTuplet[] = [];
  let beat = 0;
  while (beat < ts.beats) {
    const fitting = cells.filter((c) => c.beats <= ts.beats - beat && (c.beats === 1 || respectsGrouping(groupStarts, ts.beats, beat, c.beats)));
    const cell = fitting.length > 0 ? weightedPick(rng, fitting) : { durations: [ts.beatTicks], beats: 1, needs: [], weight: 1 };
    for (const [offset, length] of cell.tuplets ?? []) tuplets.push({ start: beat * ts.beatTicks + offset, duration: length });
    durations.push(...cell.durations);
    beat += cell.beats;
  }
  return { durations, tuplets };
}

export const RHYTHM_GENERATION = {
  /** Chance a note becomes a rest (when rests are on). */
  restChance: 0.2,
  /** Chance two notes either side of a beat or barline merge into one tied sound (when ties are on). */
  tieChance: 0.35,
  /** Candidates tried per question while looking for one with every required feature. */
  maxAttempts: 1500,
  /** Candidates after which a feature never seen is treated as impossible for the settings. */
  feasibilityAttempts: 300,
  /** Once the required features are met, how long to keep looking for a phrase not used yet. */
  noveltyAttempts: 60,
} as const;

/** Options that, when switched on, must actually show up in the practice. */
export const RHYTHM_FEATURE_OPTIONS = ['dotted', 'rests', 'ties', 'triplets'] as const;
export type RhythmFeatureOption = (typeof RHYTHM_FEATURE_OPTIONS)[number];

/**
 * Where each option's free question falls in the cycle of three. Triplets share
 * rests' slot: a rest inside a triplet satisfies both, while triplets and dotted
 * rhythms compete for beats (measured: fewest unmet requirements in short phrases).
 */
const ROTATION: Record<RhythmFeatureOption, number> = { dotted: 0, rests: 1, ties: 2, triplets: 1 };

/**
 * The switched-on options question `n` must contain. Each one is required in
 * 2 of every 3 questions; its free slot rotates, so with all options on no
 * question has to cram in every feature, and some phrases stay plain for contrast.
 */
export function requiredFeatures(config: RhythmConfig, n: number): RhythmFeatureOption[] {
  return RHYTHM_FEATURE_OPTIONS.filter((option) => config[option] && (n + ROTATION[option]) % 3 !== 2);
}

function buildPhrase(ts: TimeSignature, config: RhythmConfig, cells: Cell[], rng: Rng): RhythmPhrase {
  const durations: number[] = [];
  const tuplets: RhythmTuplet[] = [];
  const groupStarts = groupStartBeats(resolveGrouping(ts, config.grouping));
  for (let m = 0; m < config.measures; m++) {
    const measure = generateMeasure(ts, cells, rng, groupStarts);
    durations.push(...measure.durations);
    tuplets.push(...measure.tuplets.map((t) => ({ start: t.start + m * ts.measureTicks, duration: t.duration })));
  }
  const inTuplet = (tick: number) => tuplets.find((t) => tick >= t.start && tick < t.start + t.duration);
  let events: RhythmEvent[] = [];
  let cursor = 0;
  for (const duration of durations) {
    events.push({ start: cursor, duration, rest: false });
    cursor += duration;
  }
  if (config.rests) {
    // Never start the phrase with a rest: the first tap follows the count-in.
    events = events.map((e, i) => (i > 0 && rng.next() < RHYTHM_GENERATION.restChance ? { ...e, rest: true } : e));
    // A triplet keeps at least one sound: a fully silent one would just be a rest.
    for (const t of tuplets) {
      const inside = events.filter((e) => inTuplet(e.start) === t);
      if (inside.every((e) => e.rest)) inside[0]!.rest = false;
    }
  }
  if (config.ties) events = addTies(ts, config, events, rng, tuplets);
  return {
    timeSignature: ts.id,
    measures: config.measures,
    events: mergeRests(ts, events, config.dotted, inTuplet),
    ...(tuplets.length ? { tuplets } : {}),
  };
}

/**
 * Neighbouring silences become one rest event, so notation writes "quarter rest"
 * rather than two eighth rests — unless that would need a dotted rest while
 * dotted rhythms are off.
 */
function mergeRests(ts: TimeSignature, events: RhythmEvent[], allowDotted: boolean, inTuplet: (tick: number) => RhythmTuplet | undefined): RhythmEvent[] {
  const out: RhythmEvent[] = [];
  for (const e of events) {
    const last = out[out.length - 1];
    const group = last ? inTuplet(last.start) : undefined;
    // Rests merge only within the same triplet (or both outside one); inside, two thirds at most.
    const sameGroup = last !== undefined && group === inTuplet(e.start);
    const fits = group ? last!.duration + e.duration < group.duration : allowDotted || (last !== undefined && writesWithoutDots(ts, last.start, last.duration + e.duration));
    if (last && last.rest && e.rest && sameGroup && fits) last.duration += e.duration;
    else out.push({ ...e });
  }
  return out;
}

function writesWithoutDots(ts: TimeSignature, start: number, duration: number): boolean {
  let cursor = start;
  const end = start + duration;
  while (cursor < end) {
    const measureStart = Math.floor(cursor / ts.measureTicks) * ts.measureTicks;
    const segmentEnd = Math.min(end, measureStart + ts.measureTicks);
    const pieces = splitWithinMeasure(ts, cursor - measureStart, segmentEnd - measureStart);
    if (pieces.some((p) => writtenValue(p.duration)?.dots)) return false;
    cursor = segmentEnd;
  }
  return true;
}

/** Merge note pairs across beat/barline boundaries where the result is written with a tie. */
function addTies(ts: TimeSignature, config: RhythmConfig, events: RhythmEvent[], rng: Rng, tuplets: RhythmTuplet[]): RhythmEvent[] {
  const out: RhythmEvent[] = [];
  const inTuplet = (tick: number) => tuplets.some((t) => tick >= t.start && tick < t.start + t.duration);
  for (const event of events) {
    const prev = out[out.length - 1];
    const onBoundary = event.start % ts.beatTicks === 0;
    // Ties never reach into or out of a triplet.
    if (prev && !prev.rest && !event.rest && onBoundary && !inTuplet(prev.start) && !inTuplet(event.start) && rng.next() < RHYTHM_GENERATION.tieChance) {
      const merged: RhythmEvent = { start: prev.start, duration: prev.duration + event.duration, rest: false };
      const probe: RhythmPhrase = {
        timeSignature: ts.id,
        measures: config.measures,
        events: [...out.slice(0, -1), merged, ...fill(merged, ts, config)],
        ...(tuplets.length ? { tuplets: tuplets.filter((t) => t.start + t.duration <= merged.start) } : {}),
      };
      const written = engravePhrase(probe).flatMap((m) => m.items).filter((i) => i.start >= merged.start && i.start < merged.start + merged.duration);
      // Must actually read as a tie, and must not need dotted values unless they're enabled.
      if (written.length > 1 && (config.dotted || written.every((i) => i.dots === 0))) {
        out[out.length - 1] = merged;
        continue;
      }
    }
    out.push({ ...event });
  }
  return out;
}

/** Placeholder rests after `after` so a probe phrase is complete enough to engrave. */
function fill(after: RhythmEvent, ts: TimeSignature, config: RhythmConfig): RhythmEvent[] {
  const end = after.start + after.duration;
  const total = ts.measureTicks * config.measures;
  return end < total ? [{ start: end, duration: total - end, rest: true }] : [];
}

function acceptable(phrase: RhythmPhrase, ts: TimeSignature): boolean {
  const taps = expectedTapTicks(phrase);
  if (taps.length < (phrase.measures === 1 ? 2 : 3)) return false;
  // Mostly sound: rests take at most a third of the phrase, and no rest lasts longer than two beats.
  const rests = phrase.events.filter((e) => e.rest);
  if (rests.reduce((sum, e) => sum + e.duration, 0) > (ts.measureTicks * phrase.measures) / 3) return false;
  if (rests.some((e) => e.duration > ts.beatTicks * 2)) return false;
  for (let m = 0; m < phrase.measures; m++) {
    const start = m * ts.measureTicks;
    // A measure must contain at least one tap, or be continued by a tie into it.
    if (!phrase.events.some((e) => !e.rest && e.start < start + ts.measureTicks && e.start + e.duration > start)) return false;
  }
  return true;
}

function phraseKey(phrase: RhythmPhrase): string {
  return phrase.events.map((e) => `${e.rest ? 'r' : 'n'}${e.duration}`).join(' ');
}

/**
 * Strong-beats practice: one sound per tap — each group of beats (simple
 * meters, or compound meters counted in beats), or each dotted-quarter beat
 * when a compound meter is counted in eighths. Every question is the same
 * pattern; the skill is keeping the accents in time while the metronome plays
 * every pulse evenly.
 */
export function strongBeatPhrase(config: RhythmConfig): RhythmPhrase {
  const ts = getTimeSignature(config.timeSignature);
  const lengths = ts.kind === 'compound' && config.countUnit === 'eighth' ? Array.from({ length: ts.beats }, () => 1) : resolveGrouping(ts, config.grouping);
  const events: RhythmEvent[] = [];
  let cursor = 0;
  for (let m = 0; m < config.measures; m++) {
    for (const beats of lengths) {
      events.push({ start: cursor, duration: beats * ts.beatTicks, rest: false });
      cursor += beats * ts.beatTicks;
    }
  }
  return { timeSignature: ts.id, measures: config.measures, events };
}

/**
 * Generate `config.questionCount` phrases, one per question (all different when
 * possible), each containing its required features (`requiredFeatures`) when
 * the settings allow it. Assumes a validated config.
 */
export function generateRhythmPhrases(config: RhythmConfig, seed: string): RhythmPhrase[] {
  if (config.mode === 'strong-beats') return Array.from({ length: config.questionCount }, () => strongBeatPhrase(config));
  const ts = getTimeSignature(config.timeSignature);
  const cells = beatCells(ts, config);
  const rng = createRng(`rhythm:v2:${seed}`);
  const phrases: RhythmPhrase[] = [];
  const seen = new Set<string>();
  // Features some candidate has shown. One never seen after `feasibilityAttempts`
  // candidates can't occur with these settings (e.g. dotted with only quarter
  // notes), so it stops being required instead of exhausting every search.
  const observed = new Set<RhythmFeatureOption>();
  // How often each feature has made it into a chosen phrase so far.
  const delivered: Record<RhythmFeatureOption, number> = { dotted: 0, rests: 0, ties: 0, triplets: 0 };
  let candidatesBuilt = 0;
  for (let n = 0; n < config.questionCount; n++) {
    const possible = (f: RhythmFeatureOption) => observed.has(f) || candidatesBuilt < RHYTHM_GENERATION.feasibilityAttempts;
    // Best so far: most required features; when the settings force a trade-off (e.g. ties and a
    // triplet can't share two beats), the features delivered least so far; then new over repeated.
    let chosen: { phrase: RhythmPhrase; features: ReturnType<typeof phraseFeatures>; isNew: boolean } | undefined;
    const met = (c: NonNullable<typeof chosen>, required: RhythmFeatureOption[]) => required.filter((f) => c.features[f]);
    const score = (c: NonNullable<typeof chosen>, required: RhythmFeatureOption[]) => {
      const present = met(c, required);
      return present.length * 10 + present.reduce((sum, f) => sum + 1 / (1 + delivered[f]), 0) + (c.isNew ? 0.5 : 0);
    };
    for (let attempt = 0; attempt < RHYTHM_GENERATION.maxAttempts; attempt++) {
      const phrase = buildPhrase(ts, config, cells, rng);
      candidatesBuilt++;
      if (!acceptable(phrase, ts)) continue;
      const candidate = { phrase, features: phraseFeatures(phrase), isNew: !seen.has(phraseKey(phrase)) };
      for (const f of RHYTHM_FEATURE_OPTIONS) if (candidate.features[f]) observed.add(f);
      const required = requiredFeatures(config, n).filter(possible);
      if (!chosen || score(candidate, required) > score(chosen, required)) chosen = candidate;
      // Stop at a full match — a new phrase, or a repeat once novelty has had its budget
      // (some settings allow only a few different phrases).
      const featuresMet = met(chosen, required).length === required.length;
      if (featuresMet && (chosen.isNew || attempt >= RHYTHM_GENERATION.noveltyAttempts)) break;
    }
    // Fallback (very restrictive settings): steady beats, still a valid phrase.
    const phrase = chosen?.phrase ?? {
      timeSignature: ts.id,
      measures: config.measures,
      events: Array.from({ length: ts.beats * config.measures }, (_, i) => ({ start: i * ts.beatTicks, duration: ts.beatTicks, rest: false })),
    };
    for (const f of RHYTHM_FEATURE_OPTIONS) if (chosen?.features[f]) delivered[f]++;
    seen.add(phraseKey(phrase));
    phrases.push(phrase);
  }
  return phrases;
}
